/**
 * StatCard — the dashboard stat tile (Total Cases, Total Clients, etc.).
 *
 * Calm, restrained styling per the locked spec: subtle bg, tinted icon
 * chip, large number, small label.
 *
 * Props:
 *   label  — small uppercase eyebrow ("Total Cases")
 *   value  — the big number / string
 *   icon   — icon component for the chip in the corner
 *   tone   — chip color: brand | success | info | warning | danger | neutral
 *   help   — optional small caption below the value ("0% of limit")
 *   href / onClick — makes the whole card a link/button
 */

const TONES = {
  brand:   'bg-brand-soft text-brand',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger:  'bg-danger-soft text-danger',
  info:    'bg-info-soft text-info',
  neutral: 'bg-muted text-ink-secondary',
}

export default function StatCard({
  label,
  value,
  icon: Icon,
  tone = 'brand',
  help,
  href,
  onClick,
  className = '',
}) {
  const isInteractive = !!(href || onClick)
  const Wrapper = href ? 'a' : isInteractive ? 'button' : 'div'
  const interactiveCls = isInteractive
    ? 'hover:border-border-strong hover:shadow-token-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 cursor-pointer'
    : ''
  const wrapperProps = href ? { href } : isInteractive ? { type: 'button', onClick } : {}

  return (
    <Wrapper
      {...wrapperProps}
      className={`
        text-start w-full
        bg-surface border border-border rounded-xl
        p-3.5 sm:p-4 transition-all
        ${interactiveCls}
        ${className}
      `}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium uppercase tracking-wider text-ink-muted truncate">{label}</div>
          <div className="text-2xl sm:text-[1.75rem] font-semibold text-ink-primary leading-tight mt-1">{value}</div>
          {help && <div className="text-[11px] text-ink-muted mt-0.5">{help}</div>}
        </div>
        {Icon && (
          <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 ${TONES[tone] || TONES.brand}`}>
            <Icon className="w-4.5 h-4.5" aria-hidden="true" />
          </span>
        )}
      </div>
    </Wrapper>
  )
}
